import { Events } from '|>/shared/events'
import { createLogger } from '|>/shared/logger'
import { Intent } from '|>/shared/types/intent'
import { on, register } from '|>/shared/vjs'
import { BasePictureInPictureToggle } from '../base'

import './pip-button.css'

// separate logger 'player/components/pip-button' for pip-button component
const logger = createLogger('components/pip-button')

/**
 * PIP button component
 */
@register
export class PipButton extends BasePictureInPictureToggle {
  static readonly MANUAL_PIP_PAUSE_DELAY_MS = 220
  static readonly LATE_PAUSE_AFTER_LEAVE_MS = 120

  shouldBeOpened = false
  shouldResumePlayback = false
  resumeTimeoutId?: ReturnType<typeof setTimeout>
  pauseInPipTimeoutId?: ReturnType<typeof setTimeout>
  latePauseAfterLeaveTimeoutId?: ReturnType<typeof setTimeout>
  isAwaitingLatePauseAfterLeave = false

  // handle pip should be opened
  @on('loadedmetadata')
  handleLoadedMetadata() {
    this.tryRestartPip()
  }

  tryRestartPip() {
    if (this.shouldBeOpened) this.player_.requestPictureInPicture?.()
  }

  // User clicked close in PiP window
  @on('leavepictureinpicture')
  handleLeavePictureInPicture() {
    clearTimeout(this.pauseInPipTimeoutId)
    this.pauseInPipTimeoutId = undefined
    this.shouldBeOpened = false
    this.armLatePauseAfterLeave()
    logger.debug(
      'pureya/pip-button leavepictureinpicture, shouldResumePlayback=',
      {
        shouldResumePlayback: this.shouldResumePlayback,
        paused: this.player_.paused?.(),
      }
    )
    this.resumePlaybackIfNeeded(false)
  }

  // Browser can emit pause before leavepictureinpicture when closing PiP window.
  // Delay "manual pause" detection a bit so close-induced pause doesn't disable resume.
  @on('pause')
  handlePause() {
    if (this.player_.isInPictureInPicture?.()) {
      clearTimeout(this.pauseInPipTimeoutId)
      this.pauseInPipTimeoutId = setTimeout(() => {
        this.shouldResumePlayback = false
        logger.debug('pureya/pip-button manual pause in PiP detected')
      }, PipButton.MANUAL_PIP_PAUSE_DELAY_MS)
      logger.debug(
        'pureya/pip-button pause in PiP, scheduling manual pause check'
      )
      return
    }

    if (this.isAwaitingLatePauseAfterLeave && this.shouldResumePlayback) {
      logger.debug(
        'pureya/pip-button late pause right after PiP leave detected, trying to resume'
      )
      this.clearLatePauseAfterLeave()
      this.resumePlaybackIfNeeded()
    }
  }

  @on('playing')
  handlePlaying() {
    if (this.player_.isInPictureInPicture?.()) {
      clearTimeout(this.pauseInPipTimeoutId)
      this.pauseInPipTimeoutId = undefined
      this.shouldResumePlayback = true
      logger.debug('pureya/pip-button playing in PiP, auto-resume armed')
    } else if (this.shouldResumePlayback) {
      this.shouldResumePlayback = false
      this.clearLatePauseAfterLeave()
      logger.debug('pureya/pip-button playing in tab, auto-resume disarmed')
    }
  }

  // Safari WebKit fallback: presentation mode changed
  @on('webkitpresentationmodechanged')
  handleWebkitPresentationModeChanged() {
    const videoEl = this.player_.el()
      ?.firstElementChild as HTMLVideoElement | null

    if (
      !videoEl ||
      (videoEl as any).webkitPresentationMode !== 'picture-in-picture'
    ) {
      clearTimeout(this.pauseInPipTimeoutId)
      this.pauseInPipTimeoutId = undefined
      this.shouldBeOpened = false
      this.armLatePauseAfterLeave()
      logger.debug(
        'pureya/pip-button webkitpresentationmodechanged leave PiP, shouldResumePlayback=',
        {
          shouldResumePlayback: this.shouldResumePlayback,
          paused: this.player_.paused?.(),
        }
      )
      this.resumePlaybackIfNeeded(false)
    }
  }

  resumePlaybackIfNeeded(disarmOnAlreadyPlaying = true) {
    if (!this.shouldResumePlayback) return

    const tryResume = (attempt: number) => {
      if (!this.shouldResumePlayback) return
      if (this.player_.isInPictureInPicture?.()) return

      if (!this.player_.paused?.()) {
        if (disarmOnAlreadyPlaying) {
          this.shouldResumePlayback = false
          this.clearLatePauseAfterLeave()
        }
        logger.debug('pureya/pip-button resumed already, stop retries')
        return
      }

      logger.debug(
        'pureya/pip-button trying to resume playback after PiP close',
        {
          attempt,
        }
      )
      Promise.resolve(this.player_.play?.()).catch((error) => {
        logger.warn('pureya/pip-button play() rejected on PiP close', error)
      })

      if (attempt >= 2) return
      this.resumeTimeoutId = setTimeout(() => tryResume(attempt + 1), 120)
    }

    Promise.resolve().then(() => tryResume(0))
  }

  private armLatePauseAfterLeave() {
    this.clearLatePauseAfterLeave()
    if (!this.shouldResumePlayback) return

    this.isAwaitingLatePauseAfterLeave = true
    this.latePauseAfterLeaveTimeoutId = setTimeout(() => {
      this.isAwaitingLatePauseAfterLeave = false
      this.latePauseAfterLeaveTimeoutId = undefined
      logger.debug('pureya/pip-button late-pause window expired')
    }, PipButton.LATE_PAUSE_AFTER_LEAVE_MS)
  }

  private clearLatePauseAfterLeave() {
    this.isAwaitingLatePauseAfterLeave = false
    clearTimeout(this.latePauseAfterLeaveTimeoutId)
    this.latePauseAfterLeaveTimeoutId = undefined
  }

  override handleClick(event: MouseEvent) {
    event.stopPropagation()
    event.preventDefault()

    if (!this.player_.isInPictureInPicture?.()) {
      this.shouldResumePlayback = !this.player_.paused?.()
      logger.debug('pureya/pip-button click before enter PiP', {
        shouldResumePlayback: this.shouldResumePlayback,
      })
    }

    this.player_.trigger(Events.User.Intent, Intent.TogglePip)
    super.handleClick()
  }

  override dispose() {
    clearTimeout(this.resumeTimeoutId)
    clearTimeout(this.pauseInPipTimeoutId)
    clearTimeout(this.latePauseAfterLeaveTimeoutId)

    if (this.player_.isInPictureInPicture()) {
      this.shouldBeOpened = false
      this.shouldResumePlayback = false
      this.player_.exitPictureInPicture?.()
    }

    super.dispose()
  }
}

PipButton.options = {
  className: 'vjs-x-pip-button',
}
